import { LitElement, html, nothing } from 'lit';
import { customElement, property, state } from 'lit/decorators.js';
import type { Todo } from '../../types/todo.js';
import * as todoApi from '../../services/todo-api.js';
import { TodoStateController } from '../../services/todo-state.js';

@customElement('cp-todo-sub-tickets')
export class CpTodoSubTickets extends LitElement {
  createRenderRoot() { return this; }

  private _todoState = new TodoStateController(this);

  @property({ type: Number }) parentTodoId = 0;
  @property({ type: Number }) pseudoProjectId = 0;

  @state() private _subTickets: Todo[] = [];
  @state() private _loading = false;
  @state() private _newTitle = '';
  @state() private _adding = false;
  @state() private _error = '';

  connectedCallback(): void {
    super.connectedCallback();
    if (this.parentTodoId) {
      void this._load();
    }
  }

  updated(changed: Map<string, unknown>): void {
    if (changed.has('parentTodoId') && this.parentTodoId) {
      void this._load();
    }
  }

  private async _load(): Promise<void> {
    if (!this.parentTodoId) return;
    this._loading = true;
    try {
      this._subTickets = await todoApi.getSubTickets(this.parentTodoId);
    } catch {
      this._subTickets = [];
    } finally {
      this._loading = false;
    }
  }

  private _onTitleInput(e: Event): void {
    this._newTitle = (e.target as HTMLInputElement).value;
  }

  private _onKeyDown(e: KeyboardEvent): void {
    if (e.key === 'Enter') {
      void this._onAdd();
    }
  }

  private async _onAdd(): Promise<void> {
    const title = this._newTitle.trim();
    if (!title || this._adding) return;

    this._adding = true;
    this._error = '';
    try {
      await todoApi.createTodo({
        title,
        pseudo_project_id: this.pseudoProjectId,
        parent_todo_id: this.parentTodoId,
      });
      this._newTitle = '';
      await this._load();
      await this._todoState.refresh();
    } catch (e) {
      this._error = e instanceof Error ? e.message : 'Sub-Ticket konnte nicht erstellt werden';
    } finally {
      this._adding = false;
    }
  }

  private async _onToggleStatus(sub: Todo): Promise<void> {
    const newStatus = sub.status === 'done' ? 'todo' : 'done';
    this._error = '';
    try {
      await todoApi.updateTodoStatus(sub.id, newStatus);
      await this._load();
      await this._todoState.refresh();
    } catch (e) {
      this._error = e instanceof Error ? e.message : 'Status konnte nicht geaendert werden';
    }
  }

  private async _onDelete(sub: Todo): Promise<void> {
    this._error = '';
    try {
      await todoApi.deleteTodo(sub.id);
      await this._load();
      await this._todoState.refresh();
    } catch (e) {
      this._error = e instanceof Error ? e.message : 'Sub-Ticket konnte nicht geloescht werden';
    }
  }

  render() {
    if (this._loading && this._subTickets.length === 0) {
      return html`<div class="cp-sub-tickets__loading">Lade...</div>`;
    }

    return html`
      <div class="cp-sub-tickets">
        ${this._error ? html`
          <div class="cp-sub-tickets__error">${this._error}</div>
        ` : nothing}
        ${this._subTickets.length > 0 ? html`
          <ul class="cp-sub-tickets__list">
            ${this._subTickets.map(sub => html`
              <li class="cp-sub-tickets__item ${sub.status === 'done' ? 'cp-sub-tickets__item--done' : ''}">
                <button
                  class="cp-sub-tickets__checkbox ${sub.status === 'done' ? 'cp-sub-tickets__checkbox--checked' : ''}"
                  @click=${() => this._onToggleStatus(sub)}
                  title="${sub.status === 'done' ? 'Als offen markieren' : 'Als erledigt markieren'}"
                ></button>
                <span class="cp-sub-tickets__title">${sub.title}</span>
                <button
                  class="cp-sub-tickets__delete"
                  @click=${() => this._onDelete(sub)}
                  title="Loeschen"
                >&times;</button>
              </li>
            `)}
          </ul>
        ` : nothing}

        <div class="cp-sub-tickets__add">
          <input
            class="cp-sub-tickets__add-input"
            type="text"
            placeholder="Sub-Ticket hinzufuegen..."
            .value=${this._newTitle}
            @input=${this._onTitleInput}
            @keydown=${this._onKeyDown}
            ?disabled=${this._adding}
          />
          <button
            class="cp-sub-tickets__add-btn"
            @click=${this._onAdd}
            ?disabled=${!this._newTitle.trim() || this._adding}
          >+</button>
        </div>
      </div>
    `;
  }
}

declare global {
  interface HTMLElementTagNameMap {
    'cp-todo-sub-tickets': CpTodoSubTickets;
  }
}
